import {
  Dispatch,
  FC,
  forwardRef,
  SetStateAction,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import iclasses from '@pages/Admin/CRM/styles/input.module.css'
import aiclasses from '@pages/Admin/CRM/styles/activeIcon.module.css'
import gclasses from '../../../CRMSettings/global.module.css'
import { useAddAlert } from '@/hooks/useAddAlert'
import Modal from '@common/Modal'
import { IcoRemove } from '@/utils/icons'
import { Lead, LeadState } from '@/api/types/crm.types'
import { getLeadFieldValue } from '../../utils/leadFields'
import cn from 'classnames'
import { CloseOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import MainInfoBlock from './blocks/MainInfoBlock/MainInfoBlock'
import StatusListBlock from './blocks/StatusListBlock/StatusListBlock'
import EditLeadNameModal from './components/EditLeadNameModal/EditLeadNameModal'
import { Empty, Tabs, TabsProps } from 'antd'
import NarrowAboutContent from './tabs/About/NarrowAboutContent'
import WideAboutContent from './tabs/About/WideAboutContent'
import WideShoppingContent from './tabs/Shopping/WideShoppingContent'
import WideHistoryContent from './tabs/History/WideHistoryContent'
import Button from '@UI/New/Button'
import { getInArr } from '@/helpers/getInArr'
import DeleteLeadModal from '../DeleteLeadModal/DeleteLeadModal'
import ChangeLeadStateModal from '../ChangeLeadStateModal/ChangeLeadStateModal'
import { crmApi } from '@/api'
import { isError } from '@/helpers/checkQuery'
import {
  ignoreLeadFields,
  validateForm,
} from '../AddNewLeadSelect/validateLeadData'
import duplicateModalFn from './components/DuplicateModalFn/duplicateModalFn'
import TabBindingStudAccount from './components/TabBindingStudAccount/TabBindingStudAccount'
import AddStudAccountModal from './components/TabBindingStudAccount/AddStudAccountModal'
import { LeadStatusProvider } from './blocks/StatusListBlock/LeadStatusContext'
import { LeadRefetchProvider, useLeadRefetch } from './LeadRefetchContext'
import classes from './index.module.css'
import { useCRMContext } from '../../CRMContext'
import { selectProfile } from '@store/profile.reducer'
import { useSelector } from 'react-redux'
import useFullLeadModalAccess from './hooks/useFullLeadModalAccess'

interface LeadProfileModalProps {
  lead?: Lead
  open: boolean
  onClose: (withChanges: boolean) => void
  onClickByChildren: (userId: number) => void
}

export type OpenModalType =
  | 'edit-name'
  | 'delete-lead'
  | 'change-lead-state'
  | 'change-lead-state-in-client'
  | 'add-stud-account'
  | null

interface LeadProfileModalContentProps extends LeadProfileModalProps {
  leadNewType: { key: LeadState; name: string } | null
  openModal: { type: OpenModalType }
  setOpenModal: Dispatch<SetStateAction<{ type: OpenModalType }>>
  onClose: () => void
  onCloseAndRefetch: () => void
}

interface LeadProfileModalContentRef {
  handleChangeFieldData: (uniqueName: string, value: string) => void
}

const LeadProfileModalContent = forwardRef<
  LeadProfileModalContentRef,
  LeadProfileModalContentProps
>(
  (
    {
      lead,
      leadNewType,
      openModal,
      setOpenModal,
      open,
      onClose,
      onCloseAndRefetch,
      onClickByChildren,
    },
    ref,
  ) => {
    const addAlert = useAddAlert()
    const { currentRole, ...access } = useFullLeadModalAccess()
    const { handleRefetchLeadInModal, handleRefetchLeadsOnClose } = useLeadRefetch()
    const { preloadingData } = useCRMContext()

    useEffect(() => {
      handleRefetchLeadInModal()
    }, [])

    const tabs: TabsProps['items'] = [
      {
        label: 'О заявке',
        key: '1',
      },
      {
        label: 'Покупки',
        key: '2',
      },
      {
        label: 'История изменений',
        key: '3',
      },
      {
        label: !!lead && (
          <TabBindingStudAccount
            lead={lead}
            onOpenStudentAccount={() =>
              lead.user_id && onClickByChildren(lead.user_id)
            }
            onOpenAddStudAccount={() =>
              setOpenModal({ type: 'add-stud-account' })
            }
            onRefetchLead={() => {
              setOpenModal({ type: null })
              handleRefetchLeadInModal()
              handleRefetchLeadsOnClose()
            }}
          />
        ),
        key: '4',
        disabled: true,
      },
    ]

    const [selectTabKey, setSelectTabKey] = useState<string>('1')

    // fields data
    const skipFields = ['STATUS', 'DATEL', 'DATEZ']
    const [formData, setFormData] = useState<Record<string, string>>({})

    useEffect(() => {
      const defaultData =
        lead?.fields
          ?.filter((field) => !skipFields.includes(field.unique_name))
          .reduce((acc, field) => {
            acc[field.unique_name] = field.value || ''
            return acc
          }, {} as Record<string, string>) || {}

      setFormData(defaultData)
    }, [lead?.id])

    const handleChangeFieldData = (uniqueName: string, value: string) => {
      setFormData((prev) => ({
        ...prev,
        [uniqueName]: value,
      }))
    }

    useImperativeHandle(ref, () => ({
      handleChangeFieldData,
    }))

    const handlerSaveData = () => {
      if (!lead) {
        addAlert({
          title: 'Ошибка',
          text: 'Не удалось сохранить данные, лид не найден',
          type: 'error',
        })
        return
      }

      const validateFields = preloadingData.fields.filter(
        (field) => !ignoreLeadFields.includes(field.unique_name),
      )
      const resultValidation = validateForm(formData, validateFields)
      if (resultValidation !== true) {
        addAlert({
          title: 'Ошибка',
          text: Object.entries(resultValidation).map(
            ([key, value]) =>
              `${
                preloadingData.fields.find((f) => f.unique_name === key)
                  ?.name || key
              }: ${value}`,
          ),
          type: 'error',
        })
        return
      }

      crmApi
        .updateLeadFields({ lead_id: lead.id }, { fields: formData })
        .then((res) => {
          if (isError(res)) throw new Error()
          addAlert({
            title: 'Успешно',
            text: 'Данные успешно обновлены',
            type: 'success',
          })
          onCloseAndRefetch()
        })
        .catch(() => {
          addAlert({
            title: 'Ошибка',
            text: 'Ошибка обновления данных',
            type: 'error',
          })
        })
    }

    return lead ? (
      <>
        <div className={cn(mclasses.modalContainer, classes.modalContainer)}>
          <div className={mclasses.modalHeader}>
            <div className={mclasses.modalTitleContainer}>
              <span
                className={cn(
                  mclasses.modalHeaderTitle,
                  classes.modalHeaderTitle,
                )}
              >
                {formData['NAME']?.trim() || '(без фио)'}
                {access.canEditFields && (
                  <EditOutlined
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                    className={cn(gclasses.activeIcon, classes.editIcon)}
                    onClick={() => setOpenModal({ type: 'edit-name' })}
                  />
                )}
              </span>
            </div>
            <div className={mclasses.closeIcon} onClick={() => onClose()}>
              <IcoRemove />
            </div>
          </div>
          <LeadStatusProvider
            leadId={lead.id}
            onNeedUpdateLead={handleRefetchLeadsOnClose}
          >
            <div className={cn(mclasses.formWrapper, classes.formWrapper)}>
              <div className={classes.headerColumnWrapper}>
                <div className={classes.leftSection}>
                  <MainInfoBlock
                    lead={lead}
                    fieldsData={formData}
                    updateFieldData={handleChangeFieldData}
                  />
                  <div className={cn(classes.rightSection, classes.onlyMobile)}>
                    <div className={classes.rightSectionContent}>
                      <StatusListBlock
                        leadId={lead.id}
                        dateL={getLeadFieldValue(lead, 'DATEL')}
                        dateZ={getLeadFieldValue(lead, 'DATEZ')}
                      />
                    </div>
                  </div>
                  <div className={classes.customTabsWrapper}>
                    <Tabs
                      items={tabs}
                      onChange={setSelectTabKey}
                      activeKey={selectTabKey ?? undefined}
                      className={classes.customTabs}
                    />
                  </div>
                  {selectTabKey === '1' && (
                    <NarrowAboutContent
                      lead={lead}
                      fieldsData={formData}
                      updateFieldData={handleChangeFieldData}
                    />
                  )}
                </div>
                <div className={classes.rightSection}>
                  <div className={classes.rightSectionContent}>
                    <StatusListBlock
                      leadId={lead.id}
                      dateL={getLeadFieldValue(lead, 'DATEL')}
                      dateZ={getLeadFieldValue(lead, 'DATEZ')}
                    />
                  </div>
                </div>
              </div>
              {selectTabKey === '1' && (
                <WideAboutContent
                  lead={lead}
                  fieldsData={formData}
                  updateFieldData={handleChangeFieldData}
                />
              )}
              {selectTabKey === '2' && <WideShoppingContent lead={lead} />}
              {selectTabKey === '3' && <WideHistoryContent lead={lead} />}
            </div>
          </LeadStatusProvider>
        </div>

        {access.canViewControlButtons && (
          <div className={classes.controlButtonList}>
            <Button
              className={classes.controlButton}
              color="secondary"
              onClick={() => handlerSaveData()}
            >
              Сохранить
            </Button>
            <Button
              className={classes.controlButton}
              color="gray"
              onClick={() => onClose()}
            >
              Отмена
            </Button>
            {leadNewType && (
              <Button
                className={classes.controlButton}
                color="gray"
                onClick={() => setOpenModal({ type: 'change-lead-state' })}
              >
                {leadNewType?.name}
              </Button>
            )}
            {(lead.type === 'in_work' || lead.type === 'processed') && (
              <Button
                className={classes.controlButton}
                color="gray"
                onClick={() =>
                  setOpenModal({ type: 'change-lead-state-in-client' })
                }
              >
                В клиенты
              </Button>
            )}
            {access.canDelete && (
              <Button
                className={classes.controlButton}
                color="gray"
                onClick={() => setOpenModal({ type: 'delete-lead' })}
              >
                Удалить
              </Button>
            )}
          </div>
        )}
      </>
    ) : (
      <p className={classes.messageNoLead}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={'Лид не найден'}
        />
      </p>
    )
  },
)
LeadProfileModalContent.displayName = 'LeadProfileModalContent'

const LeadProfileModal: FC<LeadProfileModalProps> = (props) => {
  const [activeLead, setActiveLead] = useState<Lead | undefined>(props.lead)
  useEffect(() => setActiveLead(props.lead), [props.lead])

  return (
    <LeadRefetchProvider setLead={setActiveLead} leadId={props.lead?.id}>
      <LeadProfileModalWithContext
        {...props}
        activeLead={activeLead}
        setActiveLead={setActiveLead}
      />
    </LeadRefetchProvider>
  )
}

const LeadProfileModalWithContext: FC<
  LeadProfileModalProps & {
    activeLead: Lead | undefined
    setActiveLead: Dispatch<SetStateAction<Lead | undefined>>
  }
> = (props) => {
  const {
    open,
    onClose: defOnClose,
    lead: defLead,
    activeLead,
    setActiveLead,
  } = props

  const { handleRefetchLeadInModal, handleRefetchLeadsOnClose, state } =
    useLeadRefetch()
  const { currentRole, ...access } = useFullLeadModalAccess()

  useEffect(() => {
    if (defLead?.is_duplicate && access.canViewDuplicates)
      duplicateModalFn({
        originalLeadId: defLead.id,
        duplicateLead: defLead?.last_duplicated_lead,
        onClose: () => {},
        onSuccess: () => {
          defLead.is_duplicate = false
        },
      })
  }, [defLead])

  const leadNewType: { key: LeadState; name: string } | null =
    activeLead?.type === undefined
      ? null
      : activeLead?.type === 'in_work'
      ? {
          key: 'processed',
          name: 'В обработанные',
        }
      : activeLead?.type === 'new'
      ? {
          key: 'in_work',
          name: 'В работу',
        }
      : activeLead?.type === 'processed' || activeLead?.type === 'client'
      ? {
          key: 'in_work',
          name: 'Вернуть в работу',
        }
      : null

  const [openModal, setOpenModal] = useState<{
    type: OpenModalType
    studentId?: number
    parentId?: number
  }>({
    type: null,
  })

  const modalContentRef = useRef<LeadProfileModalContentRef>(null)

  return (
    <>
      <Modal
        active={open}
        className={cn(mclasses.modalWrapper, classes.modalWrapper) as any}
        setActive={() => defOnClose(state.needRefetchLeads)}
      >
        <LeadProfileModalContent
          {...props}
          ref={modalContentRef}
          lead={activeLead}
          leadNewType={leadNewType}
          openModal={openModal}
          setOpenModal={setOpenModal}
          onClose={() => defOnClose(state.needRefetchLeads)}
          onCloseAndRefetch={() => defOnClose(true)}
        />
      </Modal>

      {/* Modals */}
      <EditLeadNameModal
        open={openModal.type === 'edit-name'}
        name={getLeadFieldValue(activeLead, 'NAME') ?? ''}
        onClose={() => setOpenModal({ type: null })}
        onSuccess={(name) => {
          modalContentRef.current?.handleChangeFieldData('NAME', name)
          setOpenModal({ type: null })
        }}
      />
      <DeleteLeadModal
        keyTable={activeLead?.type!}
        lead={activeLead}
        open={openModal.type === 'delete-lead'}
        onClose={() => setOpenModal({ type: null })}
        onSuccess={() => {
          setOpenModal({ type: null })
          defOnClose(true)
        }}
      />
      <ChangeLeadStateModal
        newType={
          openModal.type === 'change-lead-state-in-client'
            ? 'client'
            : leadNewType?.key
        }
        userRole={currentRole}
        lead={activeLead}
        open={
          openModal.type === 'change-lead-state' ||
          openModal.type === 'change-lead-state-in-client'
        }
        onClose={() => setOpenModal({ type: null })}
        onSuccess={() => {
          setOpenModal({ type: null })
          handleRefetchLeadsOnClose()
        }}
      />
      <AddStudAccountModal
        lead={activeLead}
        open={openModal.type === 'add-stud-account'}
        onClose={() => setOpenModal({ type: null })}
        onSuccess={() => {
          setOpenModal({ type: null })
          handleRefetchLeadInModal()
          handleRefetchLeadsOnClose()
        }}
      />
    </>
  )
}

export default LeadProfileModal
